
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import QuizOption from "./QuizOption";
import QuizExplanation from "./QuizExplanation";

export interface QuizQuestionData {
  id: number;
  question: string;
  options: {
    id: string;
    text: string;
  }[];
  correctAnswer: string;
  explanation: string;
}

interface QuizQuestionProps {
  questionData: QuizQuestionData;
  onNext: (selectedOption: string, correct: boolean) => void;
}

const QuizQuestion = ({ questionData, onNext }: QuizQuestionProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);
  
  const handleSubmit = () => {
    if (!selectedOption) return;
    
    const isCorrect = selectedOption === questionData.correctAnswer;
    setAnswered(true);
  };

  const handleNext = () => {
    if (!selectedOption) return;
    const isCorrect = selectedOption === questionData.correctAnswer;
    onNext(selectedOption, isCorrect);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <h3 className="text-xl font-semibold text-[#212529] mb-6">
        {questionData.question}
      </h3>
      
      <div className="space-y-3 mb-6">
        {questionData.options.map((option) => (
          <QuizOption
            key={option.id}
            id={option.id}
            text={option.text}
            selected={selectedOption === option.id}
            correct={answered ? option.id === questionData.correctAnswer : null}
            answered={answered}
            onSelect={() => !answered && setSelectedOption(option.id)}
          />
        ))}
      </div>
      
      <QuizExplanation 
        text={questionData.explanation} 
        visible={answered}
      />
      
      {!answered ? (
        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSubmit}
            disabled={!selectedOption}
            className="bg-hero-mid hover:bg-hero-dark text-white px-8"
          >
            Submit Answer
          </Button>
        </div>
      ) : (
        <div className="flex justify-end mt-6">
          <Button
            onClick={handleNext}
            className="bg-hero-mid hover:bg-hero-dark text-white px-8"
          >
            Next Question
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      )}
    </motion.div>
  );
};

export default QuizQuestion;
